import { clearPushSession } from '../../notifications/push'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { ChowRole } from '../../../lib/actor'
import { api } from '../../../lib/api'
import { commands, isLiveRuntime } from '../../../lib/runtime'
import { useChooseChow } from '../ChooseChowContext'

/**
 * Role → (primary_app, profile class) for profile upserts. `profile:upsert`
 * overwrites primary_app and defaults absent classes to `customer`, so an edit
 * must resend the role's own app + class or it would clobber a chef/rider's
 * identity. Mirrors the backend's primaryAppForRole/userClassForRole.
 */
const ROLE_PROFILE: Record<ChowRole, { app: string; class: string }> = {
  customer: { app: 'choosechow', class: 'customer' },
  chef: { app: 'choosechow', class: 'chef' },
}

/**
 * ChooseChow session — who is signed in and as which role. The actual role
 * authority is `ChooseChowContext` (it drives dock/page context + profileId);
 * auth simply *selects* the role at sign-in and tracks session identity. A
 * bearer token is persisted under `auth_token`, which `lib/api.ts` already
 * reads onto every request. Production asks the project backend for a real
 * Foundation JWT; local development can still fall back to the prototype
 * session when the backend is not running.
 */

export interface ChowUser {
  // id is the stable auth subject (external_user_id) echoed from the JWT.
  // profileId is the chow_profiles UUID — the value ownership-scoped commands
  // expect as customer_profile_id. They are distinct; never send id as a
  // profile id.
  id?: string
  profileId?: string
  displayName: string
  /** Empty for an account that signed up with a phone number only. */
  email: string
  phone?: string
  role: ChowRole
  organizationId?: string
  avatarUrl?: string
}

export type AuthStatus = 'anonymous' | 'authenticated'

interface AuthContextValue {
  status: AuthStatus
  user: ChowUser | null
  /** `identifier` is an email address or a phone number; the server tells them apart. */
  login: (input: { identifier: string; password: string; role: ChowRole; guard?: FormGuard }) => Promise<ChowUser>
  register: (input: {
    displayName: string
    identifier: string
    password: string
    role: ChowRole
    guard?: FormGuard
  }) => Promise<ChowUser>
  /** Persist profile edits (display name / avatar / phone) and update the session. */
  updateProfile: (input: { displayName?: string; avatarUrl?: string; phone?: string }) => Promise<ChowUser>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const SESSION_KEY = 'chow_session'
const TOKEN_KEY = 'auth_token'

const readSession = (): ChowUser | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const token = localStorage.getItem(TOKEN_KEY)
    if (token && token.split('.').length === 3) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number }
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          localStorage.removeItem(SESSION_KEY)
          localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem('ops_session')
      window.dispatchEvent(new Event('chow-session-change'))
          return null
        }
      } catch {
        // malformed token; ignore and let the backend reject it later
      }
    }
    return JSON.parse(raw) as ChowUser
  } catch {
    return null
  }
}

/** Bot form guard: the honeypot value and how long the form was open (see the server's form_guard.go). */
export interface FormGuard {
  contactNote: string
  formMs: number
}

interface SessionResponse {
  user: {
    id: string
    profile_id?: string
    display_name: string
    email: string
    phone?: string
    role: ChowRole
    organization_id: string
    avatar_url?: string
  }
  access_token: string
}

const requestSession = async (
  path: '/v1/user/authenticate' | '/v1/user/register',
  input: { displayName?: string; identifier: string; password: string; role: ChowRole; guard?: FormGuard },
): Promise<{ user: ChowUser; token: string }> => {
  const resp = await api.post<SessionResponse>(path, {
    display_name: input.displayName,
    identifier: input.identifier.trim(),
    password: input.password,
    role: input.role,
    ...(input.guard ? { contact_note: input.guard.contactNote, form_ms: Math.round(input.guard.formMs) } : {}),
  })
  return {
    token: resp.access_token,
    user: {
      id: resp.user.id,
      profileId: resp.user.profile_id,
      displayName: resp.user.display_name,
      email: resp.user.email ?? '',
      phone: resp.user.phone || undefined,
      role: resp.user.role,
      organizationId: resp.user.organization_id,
      avatarUrl: resp.user.avatar_url,
    },
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { setRole, setProfileId } = useChooseChow()
  const [user, setUser] = useState<ChowUser | null>(() => readSession())

  useEffect(() => {
    const changed = () => setUser(readSession())
    window.addEventListener('chow-session-change', changed)
    window.addEventListener('storage', changed)
    return () => {
      window.removeEventListener('chow-session-change', changed)
      window.removeEventListener('storage', changed)
    }
  }, [])

  // Restore the role and profile from a persisted session on first mount.
  useEffect(() => {
    if (user) {
      setRole(user.role)
      if (user.profileId) setProfileId(user.profileId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const persist = useCallback(
    (next: ChowUser, token: string) => {
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(next))
        localStorage.setItem(TOKEN_KEY, token)
        localStorage.removeItem('ops_session')
        window.dispatchEvent(new Event('chow-session-change'))
      } catch {
        /* storage unavailable — in-memory session still works this tab */
      }
      setUser(next)
      setRole(next.role)
      if (next.profileId) setProfileId(next.profileId)
    },
    [setRole, setProfileId],
  )

  const login = useCallback<AuthContextValue['login']>(
    async ({ identifier, password, role, guard }) => {
      const session = await requestSession('/v1/user/authenticate', { identifier, password, role, guard })
      persist(session.user, session.token)
      return session.user
    },
    [persist],
  )

  const register = useCallback<AuthContextValue['register']>(
    async ({ displayName, identifier, password, role, guard }) => {
      const session = await requestSession('/v1/user/register', { displayName, identifier, password, role, guard })
      persist(session.user, session.token)
      return session.user
    },
    [persist],
  )

  const updateProfile = useCallback<AuthContextValue['updateProfile']>(
    async ({ displayName, avatarUrl, phone }) => {
      if (!user) throw new Error('not authenticated')
      const nextName = displayName?.trim() || user.displayName
      const nextAvatar = avatarUrl ?? user.avatarUrl
      const nextPhone = phone?.trim() || user.phone
      const next: ChowUser = { ...user, displayName: nextName, avatarUrl: nextAvatar, phone: nextPhone }

      // Live: persist through the profile domain. Resolve the role's own app +
      // class so the upsert never clobbers a chef/rider identity. Target the
      // caller's own row by external_user_id (the JWT subject).
      if (isLiveRuntime() && user.id) {
        const rp = ROLE_PROFILE[user.role]
        await commands.profile.upsertProfile({
          external_user_id: user.id,
          display_name: nextName,
          avatar_url: nextAvatar ?? '',
          ...(phone?.trim() ? { phone: phone.trim() } : {}),
          primary_app: rp.app,
          classes: [rp.class],
        })
      }

      setUser(next)
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(next))
      } catch {
        /* storage unavailable — in-memory update still applies this tab */
      }
      return next
    },
    [user],
  )

  const logout = useCallback(() => {
    clearPushSession()
    setUser(null)
    try {
      localStorage.removeItem(SESSION_KEY)
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem('ops_session')
      window.dispatchEvent(new Event('chow-session-change'))
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    const handleUnauthorized = () => {
      logout()
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('chow:auth:unauthorized', handleUnauthorized)
      return () => window.removeEventListener('chow:auth:unauthorized', handleUnauthorized)
    }
  }, [logout])

  const value = useMemo<AuthContextValue>(
    () => ({ status: user ? 'authenticated' : 'anonymous', user, login, register, updateProfile, logout }),
    [user, login, register, updateProfile, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
